import { useEffect } from 'react';
import { Flex } from '@backstage/ui';
import { useAsyncEntity, useEntity } from '@backstage/plugin-catalog-react';
import { RequirePermission } from '@backstage/plugin-permission-react';
import {
  AccountEntity,
  AuthType,
  catalogScanPermission,
} from '@internal/backstage-plugin-catalog-devops-common';
import {
  ButtonIconScan,
  ButtonIconSettings,
  usePrevious,
} from '@internal/backstage-plugin-devops-react';
import {
  AzureLoadFileDialog,
  ConfirmScanDialog,
  GetAwsCredentialDialog,
} from '../DialogPanels';
import {
  AwsCredential,
  AzureLoadFiles,
  useAccountContext,
} from '../Providers';
import { useIsAccountAdmin } from '../SettingsPages';

/**
 * Scan & settings icon buttons for the account header. Rendered on every
 * account tab (the overview card no longer repeats them); permission-gated:
 * scan requires catalog.scan, settings is for account admins only.
 */
export function AccountHeaderActions() {
  const { entity } = useEntity();
  const account = entity as AccountEntity | undefined;
  const { refresh } = useAsyncEntity();

  const {
    isCredentialDialogOpen,
    isConfirmDialogOpen,
    isProcessing,
    isLoading,
    authType,
    closeCredentialDialog,
    closeConfirmDialog,
    confirmStartScan,
    openConfirmDialog,
    startScanButtonClick,
    openSettings,
    setAuthType,
    setAccountEntity,
  } = useAccountContext();
  const isAdmin = useIsAccountAdmin(account);
  const previousProcessState = usePrevious(isProcessing);

  // Keeps the context's copy of the entity in sync so the scan state machine
  // works regardless of which tab the scan was started from.
  useEffect(() => {
    if (!isLoading && account) {
      setAuthType(account.spec.authType);
      setAccountEntity(account);
    }
  }, [isLoading, account, setAuthType, setAccountEntity]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isProcessing && previousProcessState && refresh) {
      refresh();
    }
  }, [isProcessing, previousProcessState, refresh]);

  const onAccessKeySubmit = (credential: AwsCredential) => {
    closeCredentialDialog();
    openConfirmDialog({ credential });
  };
  const onAzureLoadSubmit = (files: AzureLoadFiles) => {
    closeCredentialDialog();
    openConfirmDialog({ azureFiles: files });
  };

  return (
    <>
      <RequirePermission permission={catalogScanPermission} errorPage={<></>}>
        <GetAwsCredentialDialog
          isOpen={
            isCredentialDialogOpen && authType === AuthType.AWS_ACCESS_KEY
          }
          onClose={closeCredentialDialog}
          onSubmit={onAccessKeySubmit}
        />
        <AzureLoadFileDialog
          isOpen={isCredentialDialogOpen && authType === AuthType.LOAD_AZURE}
          onClose={closeCredentialDialog}
          onSubmit={onAzureLoadSubmit}
        />
        <ConfirmScanDialog
          isOpen={isConfirmDialogOpen}
          isLoad={authType === AuthType.LOAD_AZURE}
          onClose={closeConfirmDialog}
          onConfirm={confirmStartScan}
        />
      </RequirePermission>
      <Flex gap="1" align="center">
        <RequirePermission permission={catalogScanPermission} errorPage={<></>}>
          <ButtonIconScan
            variant="tertiary"
            size="medium"
            color="var(--bui-accent-bg)"
            onClick={startScanButtonClick}
            isDisabled={isProcessing}
          />
        </RequirePermission>
        {isAdmin && (
          <ButtonIconSettings
            variant="tertiary"
            size="medium"
            onClick={openSettings}
            isDisabled={isProcessing}
          />
        )}
      </Flex>
    </>
  );
}
